/**
 * Lightweight, page-local feedback (success / error toasts).
 *
 * Pairs with `<FeedbackSnackbar />`. Kept deliberately tiny and context-free so
 * each settings screen owns its own transient feedback without a global store.
 */
import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

export type FeedbackSeverity = 'success' | 'error' | 'info' | 'warning';

export interface Feedback {
  severity: FeedbackSeverity;
  message: string;
  /**
   * Optional secondary caption line.
   * Used by {@link UseFeedback.propagation} to surface an eventual-consistency
   * hint below the primary success message.
   */
  hint?: string;
}

export interface UseFeedback {
  feedback: Feedback | null;
  notify: (severity: FeedbackSeverity, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  /**
   * Show a success message with an additional "propagating to services" hint
   * below it. Use this for control-plane-backed settings (general / tenant /
   * store profile) where downstream consumers update within a few seconds.
   */
  propagation: (message: string) => void;
  clear: () => void;
}

export function useFeedback(): UseFeedback {
  const { t } = useTranslation('settings');
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const notify = useCallback((severity: FeedbackSeverity, message: string) => {
    setFeedback({ severity, message });
  }, []);

  const success = useCallback((message: string) => {
    setFeedback({ severity: 'success', message });
  }, []);

  const error = useCallback((message: string) => {
    setFeedback({ severity: 'error', message });
  }, []);

  const propagation = useCallback(
    (message: string) => {
      setFeedback({ severity: 'success', message, hint: t('useFeedback.propagationHint') });
    },
    [t],
  );

  const clear = useCallback(() => {
    setFeedback(null);
  }, []);

  return { feedback, notify, success, error, propagation, clear };
}
